package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextArea

@Composable
fun MessageInputBar(
    messageText: String,
    onTextChanged: (String) -> Unit,
    onSend: () -> Unit,
    onTyping: () -> Unit,
    isEditing: Boolean = false,
    disabled: Boolean = false,
    onAttachmentClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val hasText = messageText.isNotBlank()
    val textAreaId = remember { "msg-input-${kotlin.random.Random.nextInt(100000)}" }

    // Auto-resize textarea when text changes
    LaunchedEffect(messageText) {
        val el = document.getElementById(textAreaId) ?: return@LaunchedEffect
        el.asDynamic().style.height = "auto"
        val scrollH = el.asDynamic().scrollHeight as? Int ?: return@LaunchedEffect
        val maxH = 120
        el.asDynamic().style.height = "${minOf(scrollH, maxH)}px"
        el.asDynamic().style.overflowY = if (scrollH > maxH) "auto" else "hidden"
    }

    // Outer wrapper with horizontal padding
    Box(
        modifier = modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "${s(14)} ${s(12)}")
                property("flex-shrink", "0")
            }
    ) {
        // Input bar container with gradient border
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("min-height", s(48))
                    property("border-radius", s(28))
                }
        ) {
            // Gradient border overlay
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(28))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("min-height", s(48))
                        property("padding", "${s(8)} ${s(12)}")
                        property("gap", s(4))
                        property("align-items", "center")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Attachment icon (left) — Figma 7069:773561 paints the SVG
                // in the muted-on-surface tone #99B1BF. The asset itself uses
                // stroke="white", so we mask it and fill from a background-color.
                Box(
                    modifier = Modifier
                        .sSize(20)
                        .cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer)
                        .onClick { if (!disabled) onAttachmentClick?.invoke() }
                        .styleModifier {
                            property("flex-shrink", "0")
                            property("background-color", "#99B1BF")
                            property("mask-image", "url('${AppIcons.Attachment}')")
                            property("-webkit-mask-image", "url('${AppIcons.Attachment}')")
                            property("mask-size", "contain")
                            property("-webkit-mask-size", "contain")
                            property("mask-position", "center")
                            property("-webkit-mask-position", "center")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-repeat", "no-repeat")
                            if (disabled) property("opacity", "0.5")
                        }
                )

                // Text input (fills remaining space)
                TextArea(
                    value = messageText,
                    attrs = Modifier
                        .weight(1f)
                        .bodyLRegular()
                        .color(AppColors.Text.Primary)
                        .styleModifier {
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("resize", "none")
                            property("font-family", "Urbanist, sans-serif")
                            property("font-size", s(16))
                            property("min-height", s(24))
                            property("overflow-y", "hidden")
                            property("padding", "${s(4)} ${s(4)}")
                            property("line-height", "1.5")
                        }
                        .toAttrs {
                            attr("id", textAreaId)
                            attr("placeholder", t.chatMessageInputPlaceholder)
                            attr("rows", "1")
                            if (disabled) {
                                attr("disabled", "")
                                style {
                                    property("cursor", "not-allowed")
                                    property("opacity", "0.6")
                                }
                            }
                            onInput { event ->
                                if (disabled) return@onInput
                                val value = event.target.asDynamic().value as String
                                onTextChanged(value)
                                onTyping()
                            }
                            onKeyDown { event ->
                                if (disabled) return@onKeyDown
                                if (event.key == "Enter" && !event.shiftKey) {
                                    event.preventDefault()
                                    onSend()
                                }
                            }
                        }
                )

                // Send button — Figma 10051:166864.
                // Enabled:  bg #0061FF + white glyph.
                // Disabled: bg rgba(153,177,191,0.5) + dark navy glyph
                //           (#09142D, the panel surface), so the icon reads
                //           as a quiet inset against the muted bubble.
                Box(
                    modifier = Modifier
                        .sSize(32)
                        .sBorderRadius(100)
                        .cursor(if (disabled) Cursor.NotAllowed else Cursor.Pointer)
                        .onClick { if (!disabled && hasText) onSend() }
                        .styleModifier {
                            property("flex-shrink", "0")
                            property(
                                "background-color",
                                if (disabled) "rgba(153, 177, 191, 0.5)" else "rgb(0, 97, 255)"
                            )
                        },
                    contentAlignment = Alignment.Center
                ) {
                    val iconSrc = if (isEditing) AppIcons.Check else AppIcons.Send
                    Box(
                        modifier = Modifier
                            .sSize(16)
                            .styleModifier {
                                property("background-color", if (disabled) "#09142D" else "#FFFFFF")
                                property("mask-image", "url('$iconSrc')")
                                property("-webkit-mask-image", "url('$iconSrc')")
                                property("mask-size", "contain")
                                property("-webkit-mask-size", "contain")
                                property("mask-position", "center")
                                property("-webkit-mask-position", "center")
                                property("mask-repeat", "no-repeat")
                                property("-webkit-mask-repeat", "no-repeat")
                            }
                    )
                }
            }
        }
    }
}
